import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import ExportPDF from './ExportPDF';
import ExportCSV from './ExportCSV';

/**
 * ExportData Component
 * A combined component that provides both PDF and CSV export options
 * 
 * @param {Object} props
 * @param {Array} props.data - Array of objects containing the data to export
 * @param {Array} props.columns - Array of column definitions with key and label properties
 * @param {string} props.title - Title to display at the top of the PDF
 * @param {string} props.filename - Filename for the downloaded files (without extension)
 * @param {Array} props.visibleColumns - Optional array of column keys to include
 * @param {Array} props.formats - Array of export formats to show (default: ['PDF', 'CSV'])
 * @param {Object} props.pdfOptions - Additional PDF generation options
 * @param {Object} props.csvOptions - Additional CSV generation options
 * @param {Function} props.onSuccess - Callback function called on successful export
 * @param {Function} props.onError - Callback function called on export error
 * @param {boolean} props.disabled - Whether the export is disabled
 * @param {string} props.buttonText - Custom text for the export button
 * @param {string} props.buttonClassName - Custom CSS class for the button
 * @param {string} props.layout - Layout style: 'horizontal', 'vertical', or 'dropdown' (default: 'horizontal')
 */
const ExportData = ({
  data = [],
  columns = [],
  title = 'Data Export',
  filename = 'export',
  visibleColumns = null,
  formats = ['PDF', 'CSV'],
  pdfOptions = {},
  csvOptions = {},
  onSuccess = () => {},
  onError = () => {},
  disabled = false,
  buttonText = 'Export Data',
  buttonClassName = '',
  layout = 'horizontal'
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Handle export success
  const handleSuccess = (result) => {
    onSuccess(result);
  };

  // Handle export error
  const handleError = (result) => {
    onError(result);
  };

  // Handle dropdown toggle
  const toggleDropdown = () => {
    if (!disabled) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.export-data-container')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  // Render horizontal layout
  if (layout === 'horizontal') {
    return (
      <div className="export-data-container" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        {formats.includes('PDF') && (
          <ExportPDF
            data={data}
            columns={columns}
            title={title}
            filename={filename}
            visibleColumns={visibleColumns}
            options={pdfOptions}
            onSuccess={handleSuccess}
            onError={handleError}
            disabled={disabled}
            buttonText="Export PDF"
          />
        )}
        {formats.includes('CSV') && (
          <ExportCSV
            data={data}
            columns={columns}
            filename={filename}
            visibleColumns={visibleColumns}
            options={csvOptions}
            onSuccess={handleSuccess}
            onError={handleError}
            disabled={disabled}
            buttonText="Export CSV"
          />
        )}
      </div>
    );
  }

  // Render vertical layout
  if (layout === 'vertical') {
    return (
      <div className="export-data-container" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {formats.includes('PDF') && (
          <ExportPDF
            data={data}
            columns={columns}
            title={title}
            filename={filename}
            visibleColumns={visibleColumns}
            options={pdfOptions}
            onSuccess={handleSuccess}
            onError={handleError}
            disabled={disabled}
            buttonText="Export PDF"
          />
        )}
        {formats.includes('CSV') && (
          <ExportCSV
            data={data}
            columns={columns}
            filename={filename}
            visibleColumns={visibleColumns}
            options={csvOptions}
            onSuccess={handleSuccess}
            onError={handleError}
            disabled={disabled}
            buttonText="Export CSV"
          />
        )}
      </div>
    );
  }

  // Render dropdown layout
  return (
    <div className="export-data-container" style={{ position: 'relative' }}>
      <button
        className={`export-data-dropdown-button ${buttonClassName}`}
        onClick={toggleDropdown}
        disabled={disabled}
        style={{
          background: 'linear-gradient(135deg, #f7fbfc 0%, #edf6f8 100%)',
          color: '#134b5f',
          border: '1.5px solid #7aa7b6',
          borderRadius: '14px',
          padding: '10px 16px',
          fontWeight: 600,
          fontSize: 14,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          boxShadow: '0 10px 24px rgba(19, 75, 95, 0.12)',
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.background = 'linear-gradient(135deg, #e4f4f7 0%, #ffffff 100%)';
            e.target.style.boxShadow = '0 14px 28px rgba(19, 75, 95, 0.16)';
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled) {
            e.target.style.transform = 'translateY(0)';
            e.target.style.background = 'linear-gradient(135deg, #f7fbfc 0%, #edf6f8 100%)';
            e.target.style.boxShadow = '0 10px 24px rgba(19, 75, 95, 0.12)';
          }
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/>
          <polyline points="14,2 14,8 20,8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10,9 9,9 8,9"/>
        </svg>
        {buttonText}
        <svg 
          width="12" 
          height="12" 
          viewBox="0 0 24 24" 
          fill="currentColor"
          style={{
            transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}
        >
          <polyline points="6,9 12,15 18,9"/>
        </svg>
      </button>

      {isDropdownOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          background: '#fff',
          borderRadius: '8px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          border: '1px solid #e1e5e9',
          padding: '8px 0',
          minWidth: '200px',
          zIndex: 1000,
          animation: 'slideDown 0.2s ease-out',
        }}>
          {formats.includes('PDF') && (
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                cursor: 'pointer',
                transition: 'background 0.2s ease',
                fontSize: 14,
                color: '#335e6f',
                border: 'none',
                background: 'none',
                width: '100%',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#e6f5f7';
                e.currentTarget.style.color = '#134b5f';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
                e.currentTarget.style.color = '#335e6f';
              }}
              onClick={() => {
                setIsDropdownOpen(false);
                // Trigger PDF export
                const pdfComponent = document.createElement('div');
                pdfComponent.style.display = 'none';
                document.body.appendChild(pdfComponent);
                
                const pdfInstance = React.createElement(ExportPDF, {
                  data,
                  columns,
                  title,
                  filename,
                  visibleColumns,
                  options: pdfOptions,
                  onSuccess: handleSuccess,
                  onError: handleError,
                  disabled: false
                });
                
                ReactDOM.render(pdfInstance, pdfComponent);
                // Find and click the PDF export button
                const pdfButton = pdfComponent.querySelector('button');
                if (pdfButton) {
                  pdfButton.click();
                }
                document.body.removeChild(pdfComponent);
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#dc2626">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10,9 9,9 8,9"/>
              </svg>
              Export as PDF
            </button>
          )}
          
          {formats.includes('CSV') && (
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                cursor: 'pointer',
                transition: 'background 0.2s ease',
                fontSize: 14,
                color: '#335e6f',
                border: 'none',
                background: 'none',
                width: '100%',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#e6f5f7';
                e.currentTarget.style.color = '#134b5f';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
                e.currentTarget.style.color = '#335e6f';
              }}
              onClick={() => {
                setIsDropdownOpen(false);
                // Trigger CSV export
                const csvComponent = document.createElement('div');
                csvComponent.style.display = 'none';
                document.body.appendChild(csvComponent);
                
                const csvInstance = React.createElement(ExportCSV, {
                  data,
                  columns,
                  filename,
                  visibleColumns,
                  options: csvOptions,
                  onSuccess: handleSuccess,
                  onError: handleError,
                  disabled: false
                });
                
                ReactDOM.render(csvInstance, csvComponent);
                // Find and click the CSV export button
                const csvButton = csvComponent.querySelector('button');
                if (csvButton) {
                  csvButton.click();
                }
                document.body.removeChild(csvComponent);
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#059669">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10,9 9,9 8,9"/>
              </svg>
              Export as CSV
            </button>
          )}
        </div>
      )}

      <style>
        {`
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
};

export default ExportData;
